import {Amplify} from 'aws-amplify'
import {ResourcesConfig, fetchAuthSession, sessionStorage} from '@aws-amplify/core'
import {cognitoUserPoolsTokenProvider} from 'aws-amplify/auth/cognito'
import * as Sentry from '@sentry/browser'
import {deepClone} from '@oc/utils'

const supportedStages = ['dev', 'test', 'prod']

async function initAwsConfiguration(): Promise<string[]> {
    const stage = import.meta.env.VITE_STAGE === 'local' ? 'dev' : import.meta.env.VITE_STAGE

    if (!stage || !supportedStages.includes(stage)) {
        throw new Error(`Unknown stage ${stage}`)
    }

    const config = deepClone(await import(`./oc-shell-config.${stage}.json`)) as ResourcesConfig

    if (!config) {
        throw new Error(`No config found for stage ${stage}`)
    }

    if (import.meta.env.VITE_STAGE === 'local') {
        const url = 'http://localhost:1234/'

        config.Auth?.Cognito.loginWith?.oauth?.redirectSignIn.splice(0, 1, url)
        config.Auth?.Cognito.loginWith?.oauth?.redirectSignOut.splice(0, 1, url)
    } else if (import.meta.env.VITE_OVERRIDE_ENVIRONMENT_URL) {
        const url = `${import.meta.env.VITE_OVERRIDE_ENVIRONMENT_URL}/`

        config.Auth?.Cognito.loginWith?.oauth?.redirectSignIn.splice(0, 1, url)
        config.Auth?.Cognito.loginWith?.oauth?.redirectSignOut.splice(0, 1, url)
    }

    if (import.meta.env.VITE_OVERRIDE_CLIENT_ID && config.Auth) {
        config.Auth.Cognito.userPoolClientId = import.meta.env.VITE_OVERRIDE_CLIENT_ID
    }
    if (import.meta.env.VITE_OVERRIDE_IDENTITY_POOL_ID && config.Auth) {
        config.Auth.Cognito.identityPoolId = import.meta.env.VITE_OVERRIDE_IDENTITY_POOL_ID
    }

    Amplify.configure(config, {
        API: {
            REST: {
                headers: async () => ({
                    Authorization: `Bearer ${(await fetchAuthSession()).tokens?.idToken?.toString() as string}`
                }),
                retryStrategy: {
                    strategy: 'no-retry'
                }
            }
        }
    })
    cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage)

    return config.Auth?.Cognito.loginWith?.oauth?.redirectSignIn ?? []
}

function regexEscape(value: string): string {
    return value.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1')
}

/**
 * Inits Sentry.
 * Will ignore errors about dynamic imports of dist assets.
 * @param urls Base URLs of OC (with trailing slash)
 */
function initSentryConfiguration(urls: string[]): void {
    if (import.meta.env.VITE_STAGE !== 'local') {
        Sentry.init({
            dsn: 'https://b8d91860f4bb4a80975ae13e0af541df@sentry.tools.krones.digital/28',
            environment: import.meta.env.VITE_STAGE,
            ignoreErrors: urls.map(
                url => new RegExp(`Failed to fetch dynamically imported module: ${regexEscape(url)}assets/`, 'g')
            )
        })
    }
}

export {initAwsConfiguration, initSentryConfiguration}
